import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scale, Plus, FileText, LogOut, CreditCard, Coins } from "lucide-react";

const STATUS_COLORS = {
  Draft: "bg-gray-100 text-gray-700", Review: "bg-blue-100 text-blue-700",
  "Shared With Partner": "bg-purple-100 text-purple-700", "Under Review": "bg-amber-100 text-amber-700",
  "Changes Requested": "bg-orange-100 text-orange-700", "Ready for Execution": "bg-green-100 text-green-700",
};
const PACK_BLURBS = {
  1: "One minor revision, like small wording or clarity edits to a section.",
  5: "Add a new provision or two (such as business or real estate) with a little to spare.",
  10: "A full state-law review and update, plus several revisions or added provisions.",
  25: "Best value: a major rewrite or comprehensive redraft, with room for ongoing edits.",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [matters, setMatters] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ doc_type: "prenuptial", party1_name: user?.name || "", party2_name: "" });

  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);
  useEffect(() => {
    if (!user) return;
    api.get("/matters").then((r) => setMatters(r.data)).catch(() => {});
    api.get("/pricing").then((r) => setPricing(r.data));
    setForm((f) => ({ ...f, party1_name: user.name }));
  }, [user]);

  const create = async () => {
    try { const { data } = await api.post("/matters", form); nav(`/workspace/${data.id}`); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-[#5C6B73]">Loading…</div>;
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#E2E8F0] sticky top-0 bg-white z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Scale className="w-6 h-6 text-[#0B132B]" /><span className="font-display text-xl font-semibold text-[#0B132B]">NuPDocs</span></Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-1.5 text-sm text-[#5C6B73]" data-testid="credit-balance"><Coins className="w-4 h-4 text-[#4A6FA5]" />{user.credits || 0} credits</span>
            {user.role === "admin" && <Link to="/admin" className="text-sm text-[#4A6FA5]">Admin</Link>}
            <Link to="/billing" data-testid="billing-link" className="text-sm text-[#4A6FA5]">Billing</Link>
            <span className="text-sm text-[#5C6B73]">{user.name}</span>
            <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="text-[#5C6B73] hover:text-[#0B132B]"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl font-bold text-[#0B132B]">Your agreements</h1>
          <button onClick={() => setShowNew(true)} data-testid="new-matter-btn" className="inline-flex items-center gap-2 bg-[#0B132B] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#1C2541] transition-colors"><Plus className="w-4 h-4" />New agreement</button>
        </div>

        {user.subscription_status === "trialing" && (
          <div className="mb-6 bg-[#4A6FA5] rounded-xl p-6 flex items-center justify-between flex-wrap gap-4" data-testid="trial-banner">
            <div><div className="text-white font-semibold">You're on a 7-day free trial</div><div className="text-white/80 text-sm">Subscribe for ${pricing?.subscription_price || "18.99"}/mo to keep access when your trial ends.</div></div>
            <Link to="/billing" data-testid="trial-subscribe-btn" className="inline-flex items-center gap-2 bg-white text-[#0B132B] px-5 py-2.5 rounded-full text-sm font-medium"><CreditCard className="w-4 h-4" />Subscribe</Link>
          </div>
        )}
        {user.subscription_status !== "active" && user.subscription_status !== "trialing" && (
          <div className="mb-6 bg-[#0B132B] rounded-xl p-6 flex items-center justify-between flex-wrap gap-4" data-testid="subscribe-banner">
            <div><div className="text-white font-semibold">Activate your subscription</div><div className="text-white/70 text-sm">${pricing?.subscription_price || "18.99"}/mo, unlimited drafting &amp; sharing. Your purchased credits are safe.</div></div>
            <Link to="/billing" data-testid="subscribe-btn" className="inline-flex items-center gap-2 bg-white text-[#0B132B] px-5 py-2.5 rounded-full text-sm font-medium"><CreditCard className="w-4 h-4" />Subscribe</Link>
          </div>
        )}

        <div className="space-y-3">
          {matters.length === 0 && <div className="border border-dashed border-[#E2E8F0] rounded-xl p-12 text-center text-[#5C6B73]">No agreements yet. Create your first one.</div>}
          {matters.map((m) => (
            <Link key={m.id} to={`/workspace/${m.id}`} data-testid={`matter-${m.id}`} className="flex items-center justify-between border border-[#E2E8F0] rounded-xl p-5 hover:-translate-y-0.5 transition-transform">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F0F2F5] flex items-center justify-center"><FileText className="w-5 h-5 text-[#4A6FA5]" /></div>
                <div><div className="font-semibold text-[#0B132B]">{m.title}</div><div className="text-xs text-[#5C6B73]">{m.state || "No state selected"} · v{m.current_version}</div></div>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full ${STATUS_COLORS[m.status] || "bg-gray-100 text-gray-700"}`}>{m.status}</span>
            </Link>
          ))}
        </div>

        {pricing && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-[#0B132B]">Customization credits</h2>
              <Link to="/billing" className="text-sm text-[#4A6FA5] hover:text-[#0B132B]">Manage in Billing &amp; Credits →</Link>
            </div>
            <div className="grid sm:grid-cols-4 gap-4">
              {pricing.credit_packs.map((c) => (
                <div key={c.id} className="border border-[#E2E8F0] rounded-xl p-5 text-center">
                  <div className="font-display text-2xl font-bold text-[#0B132B]">{c.credits}</div>
                  <div className="text-xs text-[#5C6B73]">credit{c.credits > 1 ? "s" : ""} · ${c.price}</div>
                  <p className="text-xs text-[#5C6B73] leading-snug mt-2 mb-3 min-h-[48px]">{PACK_BLURBS[c.credits] || `${c.credits} credits toward custom drafting and revisions.`}</p>
                  <Link to="/billing" data-testid={`buy-credits-${c.id}`} className="block w-full text-sm border border-[#0B132B]/20 rounded-full py-2 hover:border-[#0B132B] transition-colors">Buy</Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-5" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-2xl font-bold text-[#0B132B] mb-6">New agreement</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {["prenuptial", "postnuptial"].map((t) => (
                  <button key={t} onClick={() => setForm({ ...form, doc_type: t })} data-testid={`doctype-${t}`} className={`border rounded-lg py-3 text-sm font-medium capitalize ${form.doc_type === t ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E2E8F0] text-[#0B132B]"}`}>{t}</button>
                ))}
              </div>
              <input className="w-full bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-4 py-2.5" data-testid="party1-input" placeholder="Party 1 name" value={form.party1_name} onChange={(e) => setForm({ ...form, party1_name: e.target.value })} />
              <input className="w-full bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-4 py-2.5" data-testid="party2-input" placeholder="Party 2 name" value={form.party2_name} onChange={(e) => setForm({ ...form, party2_name: e.target.value })} />
              <button onClick={create} data-testid="create-matter-submit" className="w-full bg-[#0B132B] text-white py-2.5 rounded-lg font-medium">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
